import type { MonthlyIncome } from '@sea-snag/shared';
import {
  type ChartConfig,
  ChartContainer,
  ChartLegend,
  ChartLegendContent,
  ChartTooltip,
  ChartTooltipContent,
} from '@sea-snag/ui';
import { useMemo } from 'react';
import { CartesianGrid, Line, LineChart, XAxis, YAxis } from 'recharts';

const MONTHS = [
  '',
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

// chartConfig drives both the line colours and the legend below the chart —
// the legend is what tells the user which colour is Owner / Management / Agent.
const chartConfig = {
  owner: { label: 'Owner', color: 'var(--chart-1)' },
  management: { label: 'Management', color: 'var(--chart-2)' },
  agent: { label: 'Agent', color: 'var(--chart-3)' },
} satisfies ChartConfig;

const SERIES = ['owner', 'management', 'agent'] as const;

interface IncomeMonthlyChartProps {
  monthly: MonthlyIncome[];
  currency: string;
}

export function IncomeMonthlyChart({ monthly, currency }: IncomeMonthlyChartProps) {
  const yFormatter = useMemo(
    () => new Intl.NumberFormat('en', { style: 'currency', currency, maximumFractionDigits: 0 }),
    [currency]
  );
  const data = useMemo(() => monthly.map((m) => ({ ...m, label: MONTHS[m.month] })), [monthly]);

  return (
    <ChartContainer config={chartConfig} className="aspect-auto h-[300px] w-full">
      <LineChart data={data} margin={{ top: 12, right: 16, left: 0, bottom: 0 }}>
        <CartesianGrid vertical={false} />
        <XAxis dataKey="label" tickLine={false} axisLine={false} tickMargin={8} fontSize={11} />
        <YAxis
          tickLine={false}
          axisLine={false}
          tickFormatter={(v: number) => yFormatter.format(v)}
        />
        <ChartTooltip content={<ChartTooltipContent />} />
        <ChartLegend content={<ChartLegendContent />} />
        {SERIES.map((k) => (
          <Line
            key={k}
            dataKey={k}
            type="monotone"
            stroke={`var(--color-${k})`}
            strokeWidth={2}
            dot={{ fill: `var(--color-${k})`, r: 4 }}
            activeDot={{ r: 6 }}
          />
        ))}
      </LineChart>
    </ChartContainer>
  );
}
